import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import {
  CalendarIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  CubeIcon,
  ListBulletIcon,
  TicketIcon,
  UserGroupIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Sprout } from 'lucide-react';
import { getDashboardTabs } from 'constants/navs';
import WorkspaceLogo from 'components/common/WorkspaceLogo';

const getPrimaryNavItems = t => [
  { label: t('nav.workday'), to: '/management/workday', icon: CalendarIcon },
  { label: t('nav.workers'), to: '/management/workers', icon: UsersIcon },
  { label: t('nav.activities'), to: '/management/activities', icon: ListBulletIcon },
  { label: t('nav.farmingBlock'), to: '/management/farming-block', icon: CubeIcon },
  { label: t('nav.labels'), to: '/management/stickers', icon: TicketIcon },
  { label: t('nav.crops'), to: '/management/crops', icon: Sprout },
  { label: t('nav.devices'), to: '/management/devices', icon: ComputerDesktopIcon },
  { label: t('nav.usersPermissions'), to: '/management/users', icon: UserGroupIcon },
  { label: t('nav.reports'), to: '/management/reports', icon: ChartBarIcon },
  { label: t('nav.generalSettings'), to: '/management/settings', icon: Cog6ToothIcon }
];

const DashboardNav = ({ compact = false }) => {
  const { t } = useTranslation();
  const navItems = getPrimaryNavItems(t);
  const dashboardItems = getDashboardTabs(t).map(({ label, to, icon }) => ({
    label,
    to: `/dashboard/${to}`,
    icon
  }));

  if (compact) {
    return (
      <nav className="workspace-sidebar-mobile overflow-x-auto px-4 py-4 md:hidden">
        <div className="flex w-max min-w-full gap-2">
          {[...dashboardItems, ...navItems].map(({ label, to, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `workspace-nav-link workspace-nav-link--compact inline-flex items-center gap-2 rounded-md border px-4 py-2 text-xs uppercase tracking-[0.14em] transition ${isActive ? 'workspace-nav-link--active' : ''}`
              }
            >
              <Icon className="h-4 w-4" />
              <span className="whitespace-nowrap">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    );
  }

  return (
    <aside className="workspace-sidebar fixed inset-y-0 left-0 z-50 hidden w-64 flex-col px-4 py-6 md:flex">
      <div className="mb-10 px-4">
        <a
          href="https://greenhouses.app"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('dashboard.sidebarTitle')}
          className="block"
        >
          <WorkspaceLogo
            title={t('dashboard.sidebarTitle')}
            subtitle={t('dashboard.sidebarSubtitle')}
          />
        </a>
      </div>

      <nav className="flex-1 space-y-6">
        <div className="space-y-1">
          <p className="workspace-sidebar-label px-4 font-command-label text-[10px] uppercase tracking-[0.22em]">
            {t('nav.dashboard')}
          </p>
          {dashboardItems.map(({ label, to, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `workspace-nav-link flex items-center gap-3 px-4 py-3 transition ${isActive ? 'workspace-nav-link--active' : ''}`
              }
            >
              <Icon className="h-5 w-5" />
              <span className="font-command-label text-[10px] uppercase tracking-[0.22em]">
                {label}
              </span>
            </NavLink>
          ))}
        </div>

        <div className="space-y-1">
          <p className="workspace-sidebar-label px-4 font-command-label text-[10px] uppercase tracking-[0.22em]">
            {t('nav.management')}
          </p>
          {navItems.map(({ label, to, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `workspace-nav-link flex items-center gap-3 px-4 py-3 transition ${isActive ? 'workspace-nav-link--active' : ''}`
              }
            >
              <Icon className="h-5 w-5" />
              <span className="font-command-label text-[10px] uppercase tracking-[0.22em]">
                {label}
              </span>
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="mt-auto space-y-4 pt-6">
        <button className="workspace-primary-action flex w-full items-center justify-center gap-2 rounded-md py-3 text-sm font-bold transition-transform hover:scale-[0.98]">
          <span className="text-base">+</span>
          {t('dashboard.sidebarNewActivity')}
        </button>
        <NavLink
          to="/help"
          className="workspace-help-link flex items-center gap-3 px-4 py-3 transition"
        >
          <span className="text-lg">?</span>
          <span className="font-command-label text-[10px] uppercase tracking-[0.22em]">
            {t('dashboard.sidebarHelp')}
          </span>
        </NavLink>
      </div>
    </aside>
  );
};

DashboardNav.propTypes = {
  compact: PropTypes.bool
};

DashboardNav.defaultProps = {
  compact: false
};

export default DashboardNav;
